import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { TableColumn, Link, Pill, Text } from "@foris/avocado-suite";
import { AdaptedGroup } from "@modules/sections/GroupsManager/models";
import CapacityInput from "../components/CapacityInput/CapacityInput";
import Donut from "../components/Donut/Donut";
import { Context } from "./../context/GroupsManagerContext";
import { ContextEDH } from "@context/ContextEDH";
import * as mouseflow from "@utils/mouseflow";
import { Types as NavigationTypes } from "./../context/navigation.reducer";
import { useHistory, useParams } from "react-router-dom";
import { IParams } from "@models/IParams";
import { GroupsManagerSearchOrderByFields, UserAbilities } from "@models/ISchema";
import { getColumnCell } from "@common/utils/table.util";
import GroupTableCourseColumn from "@modules/vacancies/Groups/group-table-course-column";
import css from "../components/SubgroupsTable/subgroupsTable.module.scss";

interface UseSubgroupsPackagesColumnsProps {
  abilities?: UserAbilities;
  capacitiesSumUp?: number;
  isDefaultBehavior?: boolean;
}

const isEditionAllowed = ({
  abilities,
  group,
}: {
  abilities?: UserAbilities;
  group: AdaptedGroup;
}) => {
  return group?.isEditable?.allowed && abilities?.can_edit_groups_in_group_manager;
};

export const useSubgroupsPackagesColumns = ({
  abilities,
  capacitiesSumUp = 0,
  /**
   * Default behavior is related to the initial TEC definitions of the groups manager
   */
  isDefaultBehavior = false,
}: UseSubgroupsPackagesColumnsProps) => {
  const { t } = useTranslation();
  const { state: outerState } = useContext(ContextEDH);
  const { state, dispatch } = useContext(Context);
  const { origin, scenario, workspace }: IParams = useParams();
  const contextUrl = `${workspace}/${scenario}/${origin}`;
  const history = useHistory();

  const columns: TableColumn[] = [
    {
      key: GroupsManagerSearchOrderByFields.GroupCode,
      isSortable: true,
      header: t("groups-manager.groups-table.table.headers.group-code-and-client-code"),
      renderCell: row => {
        const group = row as AdaptedGroup;
        const pathname = `/editor/groups-manager/${contextUrl}/${group?.id}`;

        if ("isResume" in group) {
          return <Text weight="medium">Cupos total</Text>;
        }

        const saveLocationStack = () => {
          dispatch({
            type: NavigationTypes.AddToLocationStack,
            payload: {
              view: `/editor/groups-manager/subgroups/${contextUrl}/${state?.data?.group?.id}`,
              page: state?.data?.groupsPageInfo?.page ?? 1,
            },
          });
          mouseflow.actionTag(
            "action_groups_manager_enrollments",
            outerState?.base?.isMouseflowEnabled,
          );
        };

        return (
          <Link
            colored
            disabled={!group?.code}
            onClick={() => {
              if (group.code) {
                saveLocationStack();
              }

              history.push(pathname);
            }}
          >
            {!group?.clientCode && !group?.code
              ? "-"
              : `${group?.code || ""}${
                  group?.clientCode && group?.code ? " | " : ""
                }${group?.clientCode || ""}`}
          </Link>
        );
      },
    },
    {
      key: GroupsManagerSearchOrderByFields.GroupType,
      header: t("groups-manager.groups-table.table.headers.group-type"),
      isSortable: true,
      renderCell: row => {
        const group = row as AdaptedGroup;
        const isPackageType = group.referentType === "PACKAGE";

        if ("isResume" in group) {
          return "";
        }

        return <Pill>{isPackageType ? "" : "No_"}Paquete</Pill>;
      },
    },
    {
      key: GroupsManagerSearchOrderByFields.Capacity,
      isSortable: true,
      header: t("groups-manager.groups-table.table.headers.capacity"),
      renderCell: row => {
        const group = row as AdaptedGroup;

        if ("isResume" in group) {
          return getColumnCell(capacitiesSumUp);
        }

        return (
          <div className={css.capacityInputCol}>
            <CapacityInput
              group={group}
              disabled={
                !isEditionAllowed({
                  abilities,
                  group,
                })
              }
              isSubgroup={true}
              inputClassname={css.capacityInput}
              disableErrorIcons
            />
          </div>
        );
      },
    },
    {
      key: "utilization",
      isSortable: false,
      header: t("groups-manager.groups-table.table.headers.utilization"),
      renderCell: row =>
        "isResume" in (row as AdaptedGroup) ? "" : <Donut group={row as AdaptedGroup} />,
    },
  ];

  if (isDefaultBehavior) {
    columns.splice(1, 0, {
      key: GroupsManagerSearchOrderByFields.CourseName,
      isSortable: false,
      header: t("groups-manager.groups-table.table.headers.course"),
      renderCell: (row: unknown) => {
        const group = row as AdaptedGroup;

        if ("isResume" in group) {
          return "";
        }

        const props = {
          groupLabel: `${group?.course?.code} - ${group?.course?.name}`,
          termCode: group?.term?.code,
          campusCode: group?.campus?.code,
          departmentCode: group?.curriculum?.program?.department?.code,
          modalityCode: group?.curriculum?.program?.modality?.code,
          shiftCode: group?.curriculum?.program?.shift?.code,
          programCode: group?.curriculum?.program?.code,
          curriculumCode: group?.curriculum?.code,
        };

        return <GroupTableCourseColumn {...props} hasOnboarding={false} />;
      },
      desktopOptions: {
        headCellStyles: {
          width: "380px",
          minWidth: "100px",
        },
      },
    });
  }

  return [columns];
};
